/**
 * DocApplicabilityChips — DAT-1 (Document Applicability Transparency).
 *
 * Compact reasoning strip for a Documents-tab row. Surfaces:
 *
 *   - "Populates" — total count of SED variables the doc populated
 *     (UPE-cached `populatedSlotCount`).
 *   - MIME conflict warnings when applicable.
 *   - "[Show detail]" — opens the DocLineageInspector (DAT-2/3) with
 *     the full breakdown.
 *
 * Renders nothing when there are no populated slots and no warnings —
 * keeps untouched rows compact.
 */
import React from 'react';
import type { SiteDocument } from '../../types/siteRag';
import type { MimeSanityWarning } from '../../lib/docMimeSanity';

interface Props {
  doc: SiteDocument;
  /** Click handler for the [Show detail] button. Receives the doc id
   *  so the parent can mount a single DocLineageInspector and toggle
   *  by docId. */
  onShowDetail?: (docId: string) => void;
  /**
   * DH-2 — DMS cross-doc MIME mismatch warnings for THIS doc. Authored
   * by `computeMimeSanityWarnings` (called once per site doc set in
   * the parent), then sliced per-doc and threaded in via this prop.
   * The chip strip renders one amber warning chip per unique
   * requirement (de-duped across multiple conflicting peers, with the
   * peer count surfaced in the tooltip). Absent / empty = no warning.
   */
  mimeSanityWarnings?: readonly MimeSanityWarning[];
}

export const DocApplicabilityChips: React.FC<Props> = ({
  doc,
  onShowDetail,
  mimeSanityWarnings,
}) => {
  const slotCount = doc.populatedSlotCount ?? 0;

  // DH-2 — group warnings by requirementId so the same requirement with
  // multiple conflicting peers renders as a single chip ("conflicts with N
  // other docs"). Order-stable on first appearance.
  const groupedWarnings = React.useMemo(() => {
    const map = new Map<string, MimeSanityWarning[]>();
    for (const w of mimeSanityWarnings ?? []) {
      const list = map.get(w.requirementId) ?? [];
      list.push(w);
      map.set(w.requirementId, list);
    }
    return [...map.entries()];
  }, [mimeSanityWarnings]);

  if (slotCount === 0 && groupedWarnings.length === 0) return null;

  return (
    <div
      className="flex items-center gap-1 flex-wrap mt-1"
      data-testid={`doc-applicability-${doc.id}`}
    >
      <span className="text-[10px] text-text-muted uppercase tracking-wider">Populates:</span>
      <span
        className="type-meta px-1.5 py-0.5 border border-border text-text-secondary"
        data-testid={`doc-applicability-${doc.id}-slot-count`}
        title={slotCount === 0
          ? 'Document was classified but did not populate any SED variables.'
          : `Doc populated ${slotCount} SED variable${slotCount === 1 ? '' : 's'}.`}
      >
        {slotCount} SED var{slotCount === 1 ? '' : 's'}
      </span>
      {groupedWarnings.length > 0 && (
        <>
          <span className="text-[10px] text-warning uppercase tracking-wider ml-2">
            Conflicts:
          </span>
          {groupedWarnings.map(([requirementId, group]) => {
            const first = group[0];
            if (!first) return null;
            const peerCount = group.length;
            const peerNames = group.map(g => `${g.otherDocFileName} (${g.otherFamily})`).join(', ');
            const title =
              `MIME mismatch on "${first.requirementLabel}":\n` +
              `This doc is ${first.thisFamily.toUpperCase()}; ` +
              `${peerCount === 1 ? 'another doc' : `${peerCount} other docs`} claim the same requirement with a different shape — ${peerNames}.\n` +
              `The resolver may pick the wrong source. Review which upload is the true evidence.`;
            return (
              <span
                key={`mime-warn-${requirementId}`}
                className="type-meta px-1.5 py-0.5 rounded-md border border-warning/40 text-warning bg-warning/5"
                title={title}
                data-testid={`doc-applicability-${doc.id}-mime-warning-${requirementId}`}
                data-mime-warning="true"
              >
                {first.requirementLabel}
                <span className="ml-1 type-body opacity-70">MIME?</span>
              </span>
            );
          })}
        </>
      )}
      {onShowDetail && (
        <button
          type="button"
          onClick={() => onShowDetail(doc.id)}
          className="text-[10px] text-primary underline ml-1 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-primary"
          data-testid={`doc-applicability-${doc.id}-show-detail`}
        >
          Show detail
        </button>
      )}
    </div>
  );
};
